import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { apiClient } from '../../lib/api';

// Initialize Stripe with environment-specific publishable key
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SFdlMHyvgVPioWP1Fk0RXUsWsyKEfsIHmiuTGTO0eD00fPhPDJ6ypxkGBCJ0vEPyOWtsOFNRy1byiX8KxkNkexd00WYBItWR6';
const stripePromise = loadStripe(stripePublishableKey);

interface StripePaymentProps {
  tenantId: string;
  plan: string;
  planName: string;
  amount: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface CheckoutFormProps extends StripePaymentProps {
  clientSecret: string;
}

function CheckoutForm({ planName, amount, onSuccess, onCancel, clientSecret }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handlePayment = async () => {
    if (!stripe || !elements || !clientSecret) {
      setError('Payment system not ready');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Payment failed');
        return;
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/billing',
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
      } else if (setupIntent?.status === 'succeeded') {
        // Show success message and wait a moment for webhook processing
        setError(null);
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 3000); // Wait 3 seconds for webhook to process
      } else {
        setError('Payment setup incomplete. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Upgrade to {planName}
          <Badge variant="secondary">${amount}/month</Badge>
        </CardTitle>
        <CardDescription>
          Set up your payment method to start your subscription
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">What you'll get:</h4>
          <ul className="text-sm space-y-1 text-blue-700">
            <li className="flex items-center gap-2">
              <span className="text-blue-500">✓</span>
              Full access to {planName} features
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-500">✓</span>
              No more trial limitations
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-500">✓</span>
              Monthly billing starting today
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-500">✓</span>
              Cancel anytime
            </li>
          </ul>
        </div>

        {error && (
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <PaymentElement />
        </div>

        {success && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <span className="text-green-500 text-lg">✓</span>
              <div>
                <p className="text-green-700 text-sm font-medium">
                  Payment successful! Setting up your subscription...
                </p>
                <p className="text-green-600 text-xs">
                  You'll be redirected to your billing page in a moment.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button 
            onClick={handlePayment}
            disabled={processing}
            className="flex-1"
          >
            {processing ? 'Processing...' : `Subscribe $${amount}/month`}
          </Button>
          <Button 
            variant="outline" 
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function StripePayment(props: StripePaymentProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const paymentData = await apiClient.createPaymentIntent({
          amount: Math.round(props.amount * 100),
          currency: 'usd',
          tenant_id: props.tenantId,
          plan: props.plan
        });
        setClientSecret(paymentData.client_secret);
      } catch (err) {
        setError('Failed to initialize payment');
      }
    };
    createPaymentIntent();
  }, [props.amount, props.tenantId, props.plan]);

  if (error) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="p-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <Button onClick={props.onCancel} className="mt-4 w-full">
            Back to Billing
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!clientSecret) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="p-6 text-center">
          <p>Loading payment form...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm {...props} clientSecret={clientSecret} />
    </Elements>
  );
}